import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClock, faArrowDown, faArrowUp, faScaleBalanced, faTrashCan, faLocationDot,
  faPersonRunning, faPersonBiking, faPersonSwimming, faPersonWalking, faBullseye, faBolt, faDumbbell } from "@fortawesome/free-solid-svg-icons";
import styles from "../pages/Dashboard.module.css";
import LocationMapModal from "./LocationMapModal";

const cn = (...classes) => classes.filter(Boolean).join(" ");

function _norm(str) {
  return (str || '').toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9 ]/g, '');
}
function shortLocationLabel(label) {
  if (!label) return null;
  // geocode_service.py junta "Nome do local – Bairro/Cidade" — no badge da
  // lista mostramos só a primeira parte, curta o bastante pra não estourar.
  return label.split(" – ")[0];
}

function getCardioIcon(name) {
  const h = _norm(name);
  if (/bicicleta|bike|cicl|spinning/.test(h))    return faPersonBiking;
  if (/natacao|nadar|swim|aqua|piscina/.test(h))  return faPersonSwimming;
  if (/caminhada|walk|andar/.test(h))             return faPersonWalking;
  if (/core|abdomen|abdomin|prancha|plank/.test(h)) return faBullseye;
  if (/hiit|tabata|circuito|funcional/.test(h))   return faBolt;
  if (/remo|rowing|muscula/.test(h))               return faDumbbell;
  return faPersonRunning;
}



/**
 * Lista os últimos 5 check-ins com indicadores de desempenho vs. duração esperada.
 *
 * @param {Array}    items            - lista de check-ins
 * @param {Array}    exercises        - lista de exercícios (legado, mantido para compat.)
 * @param {Array}    assignedWorkouts - planos atribuídos (para calcular duração esperada)
 * @param {function} onSessionClick   - (sessionId, checkin) => void — abre detalhe da sessão
 * @param {function} onDeleteCheckin  - (checkinId, userId) => void — deleta check-in
 * @param {string|number} userId
 * @param {boolean}  canEditLabel     - repassado pro LocationMapModal: só true quando quem
 *                      está logado é professor/admin (nunca o aluno vendo o próprio
 *                      check-in) — ver comentário de canEditLabel em LocationMapModal.jsx
 */
export default function CheckinsTimeline({
  items,
  exercises,
  assignedWorkouts = [],
  onSessionClick,
  onDeleteCheckin,
  userId,
  canEditLabel = false,
}) {
  const [deletingId, setDeletingId]       = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [mapModal, setMapModal] = useState(null); // { checkinId, lat, lon, label, dateLabel }
  // Correções de nome de local feitas nesta sessão (checkinId -> label) —
  // `items` vem de props do pai, não dá pra mutar direto; sobrepõe só a
  // exibição local até o próximo refetch trazer o valor já persistido.
  const [labelOverrides, setLabelOverrides] = useState({});

  const lastItems      = [...items].sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 5);
  const totalDuration  = lastItems.reduce((sum, c) => sum + Number(c.duration || 0), 0);
  const averageDuration = lastItems.length > 0 ? Math.round(totalDuration / lastItems.length) : 0;

  const getPlanExpectedMinutes = (userWorkoutPlanId) => {
    if (!userWorkoutPlanId) return null;
    const assignment = assignedWorkouts.find((a) => String(a.id) === String(userWorkoutPlanId));
    if (!assignment) return null;
    const total = (assignment.items || []).reduce(
      (sum, item) => sum + Number(item.duration_planned || 0),
      0
    );
    return total > 0 ? total : null;
  };

  const spDateTime = (iso) =>
    new Intl.DateTimeFormat("pt-BR", {
      timeZone: "America/Sao_Paulo",
      dateStyle: "short",
      timeStyle: "short",
    }).format(new Date(iso));

  return (
    <div className={styles.timeline}>
      <div className={styles.timelineHeader}>
        <h2 className={styles.h2}>Últimos 5 Checkins</h2>
        {lastItems.length > 0 && (
          <span className={styles.timelineMeta}>Média de tempo: {averageDuration} min</span>
        )}
      </div>

      {lastItems.length === 0 && (
        <p className={styles.muted}>Nenhum check-in realizado ainda</p>
      )}

      <div className={styles.timelineList}>
        {lastItems.map((c) => {
          const rawTitle      = c.snapshot_workout_plan_title || c.workout_plan_title || c.workout_plan?.title || null;
          const exerciseName  = c.snapshot_exercise_name || c.exercise?.name || null;
          const isManual      = !rawTitle;
          const workoutTitle  = (rawTitle || exerciseName || "Atividade livre").replace(/[—–]/g, "-");
          const workoutIcon   = isManual ? getCardioIcon(exerciseName) : null;

          const actual        = Number(c.duration);
          const expected      = getPlanExpectedMinutes(c.user_workout_plan_id);

          let perf        = { icon: faClock, colorClass: styles.perfNeutral, label: "Sem referência" };
          let percentText = null;

          if (expected != null && expected > 0 && !isNaN(actual) && actual > 0) {
            const ratio   = actual / expected;
            const percent = Math.round(ratio * 100);
            if (ratio < 0.85)       perf = { icon: faArrowDown, colorClass: styles.perfBad,     label: "Abaixo do esperado" };
            else if (ratio > 1.15)  perf = { icon: faArrowUp,   colorClass: styles.perfGood,    label: "Acima do esperado"  };
            else                    perf = { icon: faClock,      colorClass: styles.perfNeutral, label: "Dentro do esperado" };
            percentText = `${percent}% do esperado`;
          }

          const formattedDate  = spDateTime(c.date);
          const itemsList      = Array.isArray(c.snapshot_plan_items) ? c.snapshot_plan_items : [];
          const itemsPreview   = itemsList.slice(0, 4).map((it) => it?.name || "").filter(Boolean).join(" • ");
          const points         = c.points_awarded ?? c.points ?? 0;
          const pointsClass    = points <= 0 ? styles.pointsBad : points > 10 ? styles.pointsGood : points < 10 ? styles.pointsGood : styles.pointsNeutral;
          const sessionId      = c.workout_session_id || null;
          const isClickable    = !!onSessionClick;
          const volKg          = c.total_volume_kg || null;
          const volLabel       = volKg ? `${Math.round(volKg).toLocaleString("pt-BR")}kg` : null;
          const handleClick    = isClickable ? () => onSessionClick(sessionId, c) : undefined;

          return (
            <div
              key={c.id}
              className={cn(styles.timelineItem, isClickable && styles.timelineItemClickable)}
              onClick={handleClick}
              role={isClickable ? "button" : undefined}
              tabIndex={isClickable ? 0 : undefined}
              onKeyDown={
                isClickable
                  ? (e) => { if (e.key === "Enter" || e.key === " ") handleClick(); }
                  : undefined
              }
            >
              <div className={cn(styles.timelineIcon, perf.colorClass)}>
                <FontAwesomeIcon icon={perf.icon} />
              </div>

              <div className={styles.timelineBody}>
                <strong className={styles.timelineTitle}>
                  {workoutIcon && (
                    <FontAwesomeIcon icon={workoutIcon} className={styles.timelineTitleIcon} />
                  )}
                  {workoutTitle}
                  {isClickable && <span className={styles.timelineDetailHint}> →</span>}
                </strong>
                {volLabel && (
                  <div className={styles.timelineVolume}>
                    <FontAwesomeIcon icon={faScaleBalanced} className={styles.timelineVolumeIcon} />
                    {volLabel} vol. total
                  </div>
                )}
                <div className={styles.timelineSub}>
                  {formattedDate} • {actual} min
                </div>
                {c.checkin_lat != null && c.checkin_lon != null && (
                  <button
                    type="button"
                    className={styles.timelineLocation}
                    onClick={(e) => {
                      e.stopPropagation();
                      setMapModal({
                        checkinId: c.id,
                        lat: c.checkin_lat,
                        lon: c.checkin_lon,
                        label: labelOverrides[c.id] ?? c.checkin_location_label,
                        dateLabel: formattedDate,
                      });
                    }}
                  >
                    <FontAwesomeIcon icon={faLocationDot} className={styles.timelineLocationIcon} />
                    <span>{shortLocationLabel(labelOverrides[c.id] ?? c.checkin_location_label) || "Ver localização"}</span>
                  </button>
                )}
                {itemsPreview && (
                  <div className={styles.timelineMeta}>
                    {itemsPreview}{itemsList.length > 4 ? "…" : ""}
                  </div>
                )}
                {percentText && (
                  <div className={cn(styles.timelinePerf, perf.colorClass)}>{percentText}</div>
                )}
              </div>

              <div className={styles.timelineRight}>
                <div className={cn(styles.timelinePoints, pointsClass)}>
                  {points > 0 && "+"}{points} pts
                </div>
                {onDeleteCheckin && (
                  confirmDeleteId === c.id ? (
                    <div
                      className={styles.timelineDeleteConfirm}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <span className={styles.timelineDeleteConfirmLabel}>Excluir?</span>
                      <button
                        className={styles.timelineDeleteConfirmYes}
                        disabled={deletingId === c.id}
                        onClick={async (e) => {
                          e.stopPropagation();
                          setDeletingId(c.id);
                          setConfirmDeleteId(null);
                          try {
                            await onDeleteCheckin(c.id, userId);
                          } catch (err) {
                            console.error("Erro ao excluir check-in:", err);
                          } finally {
                            setDeletingId(null);
                          }
                        }}
                      >
                        {deletingId === c.id ? "…" : "Sim"}
                      </button>
                      <button
                        className={styles.timelineDeleteConfirmNo}
                        onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(null); }}
                      >
                        Não
                      </button>
                    </div>
                  ) : (
                    <button
                      className={styles.timelineDeleteBtn}
                      title="Excluir check-in"
                      disabled={deletingId === c.id}
                      onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(c.id); }}
                    >
                      <FontAwesomeIcon icon={faTrashCan} />
                    </button>
                  )
                )}
              </div>
            </div>
          );
        })}
      </div>

      {mapModal && (
        <LocationMapModal
          lat={mapModal.lat}
          lon={mapModal.lon}
          label={mapModal.label}
          dateLabel={mapModal.dateLabel}
          checkinId={mapModal.checkinId}
          userId={userId}
          canEditLabel={canEditLabel}
          onLabelUpdated={(newLabel) =>
            setLabelOverrides((prev) => ({ ...prev, [mapModal.checkinId]: newLabel }))
          }
          onClose={() => setMapModal(null)}
        />
      )}
    </div>
  );
}
